import { T } from '@/ui/Text/Text';
import { Flex } from '@/ui/Flex/Flex';
import React from 'react';
import { useIntl } from 'react-intl';
import { IconBuzzerPrimary, IconChannelSelective, IconLanguage } from '@/icons';
import HorizontalDivider from '@/ui/HorizontalDivider';
import CustomSelect, { MultiSelect } from '@/ui/Select/Select';
import { useNodes } from '@xyflow/react';
import { SensorBuzzerNodeType } from '../../Nodes/types';
import FAQSection from '../FAQSection';
import { StyledSettings, StyledSettingsContent } from '../styles';
import { GeneralSettingsType, LanguageOptions } from '../types';
import { generateBuzzerOptions } from '../../util/generateSettingsOptions';
import { findLanguageAnyNodes } from '../../util/findLanguageAnyNodes';

function BuzzerSettings({
  settings,
  selectedNode,
  updateNodeData,
  selectedBuzzers,
  setSelectedSensors,
  disabled,
}: {
  settings: GeneralSettingsType;
  selectedNode: SensorBuzzerNodeType;
  updateNodeData: (id: string, newData: any) => void;
  selectedBuzzers: string[];
  setSelectedSensors: React.Dispatch<
    React.SetStateAction<{
      buttons: string[];
      buzzers: string[];
      trihows: string[];
    }>
  >;
  disabled?: boolean;
}) {
  const intl = useIntl();

  // get all buzzers that have "Any" as language to filter the options accordingly
  const nodes = useNodes();
  const { languageAnyBuzzers } = findLanguageAnyNodes(nodes);

  const faqs = [
    ...(settings.languageSelective
      ? [
          {
            question: intl.formatMessage({
              defaultMessage: 'What is the purpose of Language in this block?',
              id: 'Yg3yKs',
            }),
            answer: intl.formatMessage({
              defaultMessage:
                'Language defines under which language the logic for this specific buzzer should work. If it is set to "Any", the logic for this buzzer will work on anylanguage.',
              id: '+3AXpk',
            }),
          },
        ]
      : []),
  ];
  const relevantData = selectedNode.data.options;

  const channelOptions = generateBuzzerOptions(
    settings.languageSelective,
    settings.buzzers,
    selectedBuzzers,
    relevantData.buzzers ?? [],
    languageAnyBuzzers,
  );

  return (
    <StyledSettings data-testid="sensor-buzzer-settings">
      <Flex align="center" gap={8}>
        <IconBuzzerPrimary />
        <Flex direction="column">
          <T h3>
            {intl.formatMessage({ defaultMessage: 'Buzzer', id: '8oBAWC' })}
          </T>
        </Flex>
      </Flex>
      <StyledSettingsContent>
        <Flex direction="column" gap={8}>
          <MultiSelect
            disabled={disabled ?? false}
            required
            title={intl.formatMessage({
              defaultMessage: 'Buzzer',
              id: '8oBAWC',
            })}
            placeholder={intl.formatMessage({
              defaultMessage: 'Select Buzzer(s)',
              id: 'WM4hy9',
            })}
            icon={<IconChannelSelective />}
            options={channelOptions}
            size="small"
            onChange={(e) => {
              updateNodeData(selectedNode.id, {
                ...selectedNode.data,
                options: {
                  ...relevantData,
                  buzzers: e
                    ? e.map((item) => ({
                        triggerId: item.value,
                        name: item.label,
                      }))
                    : null,
                },
              });
              setSelectedSensors((prev) => {
                // Get previous buzzers selected by this node
                const prevNodeBuzzers = Array.isArray(relevantData.buzzers)
                  ? relevantData.buzzers.map((b) => b.triggerId)
                  : [];

                // Get new buzzers selected by this node
                const newNodeBuzzers: string[] = Array.isArray(e)
                  ? e.map((entry) => entry.value)
                  : [];

                // Remove buzzers previously selected by this node
                const withoutPrevNodeBuzzers = prev.buzzers.filter(
                  (buzzer) => !prevNodeBuzzers.includes(buzzer),
                );
                // Add new buzzers selected by this node
                const updatedBuzzers = [
                  ...withoutPrevNodeBuzzers,
                  ...newNodeBuzzers,
                ];
                // Remove duplicates
                const uniqueBuzzers = Array.from(new Set(updatedBuzzers));
                return {
                  ...prev,
                  buzzers: uniqueBuzzers,
                };
              });
            }}
            value={
              relevantData.buzzers && relevantData.buzzers.length > 0
                ? relevantData.buzzers.map((t) => ({
                    label: t.name,
                    value: t.triggerId,
                  }))
                : null
            }
          />
          {settings.languageSelective && (
            <Flex justify="space-between" align="center">
              <T body1>
                {intl.formatMessage({
                  defaultMessage: 'Works in Language',
                  id: 'M+Dbq2',
                })}
              </T>
              <CustomSelect
                disabled={disabled ?? false}
                size="small"
                options={LanguageOptions.map((lang) => ({
                  label: lang.value
                    ? lang.value
                    : intl.formatMessage({
                        defaultMessage: 'Any',
                        id: 'ToO3/h',
                      }),
                  value: lang.value,
                }))}
                value={
                  relevantData.language
                    ? {
                        label: relevantData.language,
                        value: relevantData.language,
                      }
                    : {
                        label: intl.formatMessage({
                          defaultMessage: 'Any',
                          id: 'ToO3/h',
                        }),
                        value: null,
                      }
                }
                onChange={(e) => {
                  updateNodeData(selectedNode.id, {
                    ...selectedNode.data,
                    options: { ...relevantData, language: e?.value },
                  });
                }}
                placeholder={intl.formatMessage({
                  defaultMessage: 'Language',
                  id: 'y1Z3or',
                })}
                isSearchable={false}
                icon={<IconLanguage />}
              />
            </Flex>
          )}
        </Flex>
        <HorizontalDivider />
      </StyledSettingsContent>
      <FAQSection faqs={faqs} />
    </StyledSettings>
  );
}

export default BuzzerSettings;
